import { AvailableExtra, Currency } from '~/lib/types/basket-types';
import {
  P2GAlert,
  P2GBox,
  P2GFade,
  P2GGrid,
  P2GImage,
  P2GLink,
  P2GRadio,
  P2GText,
  useP2GMediaQueryOnly
} from '@parcel2go/core-ui';
import { Trans, useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { ParcelProtectionInfo } from './protection-info';
import { Suspense, useCallback, useMemo, useState } from 'react';
import { ProtectionMessage } from './protection-message';
import P2GOptionContainer from '~/components/core/option/option';
import SmoothLoader from '~/components/core/smooth/smooth-loader';
import { Protection } from '~/lib/types/extra-types';
import React from 'react';
import { ParcelProtectionTitle } from './protection-title';

const CmsContent = React.lazy(() => import('~/components/core/content/cms-content'));

type ParcelProtectionProps = {
  currency: Currency;
  selected: AvailableExtra | undefined;
  onChange: (extra: string, selected: boolean) => void;
  errors?: Record<string, string>;
  options: AvailableExtra[];
  parcelValue: number;
  includedFreeOrBaseProtectionAmount: number;
  serviceIncludedProtection: number | undefined;
  maxProtection: number;
  quoting: boolean;
  dataTestId: string;
  userHasFullyProtected: boolean;
  isFullyProtectedByIncludedOrBaseProtection: boolean;
  quoteHasErrors: boolean;
};

const radioGroup = 'protection';

export const ParcelProtection = ({
  currency,
  selected,
  onChange,
  errors,
  options,
  serviceIncludedProtection,
  parcelValue,
  quoting,
  maxProtection,
  dataTestId,
  userHasFullyProtected,
  includedFreeOrBaseProtectionAmount,
  isFullyProtectedByIncludedOrBaseProtection,
  quoteHasErrors
}: ParcelProtectionProps) => {
  const { t } = useTranslation([TranslationNamespaces.CHECKOUT]);
  const isXs = useP2GMediaQueryOnly('xs');
  const [open, setOpen] = useState(false);
  const translations = useMemo(
    () => ({
      recommendedText: t('PROTECTION_RECOMMENDED', { defaultValue: 'RECOMMENDED' })
    }),
    [t]
  );

  const isRecommended = useCallback(
    (o: AvailableExtra) => {
      if (quoting || quoteHasErrors) {
        return false;
      }
      if (o.key === Protection.FULL) {
        return true;
      }
      if ((o.metadata?.protectedAmount || 0) > 0) {
        return o.key !== Protection.NONE && parcelValue <= (o.metadata?.protectedAmount || 0);
      }
      return false;
    },
    [parcelValue, quoting, quoteHasErrors]
  );

  const protectionNotAvailable = options.every((x) => x.key === Protection.NONE);
  const showOptions = !quoteHasErrors && parcelValue > 0 && options.length > 0;
  const showMessage = !quoteHasErrors && !isFullyProtectedByIncludedOrBaseProtection && !protectionNotAvailable;

  return (
    <SmoothLoader loading={quoting}>
      <P2GGrid container spacing={2} dataTestId="parcel-protection-radio-buttons">
        {isXs && (
          <Suspense>
            <CmsContent
              contentKey="protection-shield"
              wrapper={(x) => (
                <P2GGrid item xs={12}>
                  <P2GBox textAlign="center">{x}</P2GBox>
                </P2GGrid>
              )}
            >
              <P2GImage
                src="https://content.parcelsolutions.net/42c5534b-0f47-4342-80f0-8f21203f0669/order-process/protection-shield.png"
                alt="Protection"
                style={{ height: '110px' }}
              />
            </CmsContent>
          </Suspense>
        )}
        <P2GGrid item xs={12}>
          <P2GBox mb={2}>
            <P2GGrid container alignItems="center" gap={2} wrap="nowrap">
              {!isXs && (
                <Suspense>
                  <CmsContent
                    contentKey="protection-shield"
                    wrapper={(x) => (
                      <P2GGrid item flexShrink={1}>
                        {x}
                      </P2GGrid>
                    )}
                  >
                    <P2GImage
                      src="https://content.parcelsolutions.net/42c5534b-0f47-4342-80f0-8f21203f0669/order-process/protection-shield.png"
                      alt="Protection"
                      style={{ height: '80px' }}
                    />
                  </CmsContent>
                </Suspense>
              )}
              <P2GGrid item flexGrow={1}>
                <Suspense>
                  <ParcelProtectionTitle
                    currency={currency}
                    option={options[0]}
                    parcelValue={parcelValue}
                    maxProtection={maxProtection}
                    dataTestId={dataTestId}
                    includedFreeOrBaseProtectionAmount={includedFreeOrBaseProtectionAmount}
                    isFullyProtectedByIncludedOrBaseProtection={isFullyProtectedByIncludedOrBaseProtection}
                    protectionNotAvailable={protectionNotAvailable}
                    showMessage={showMessage}
                  />
                </Suspense>
              </P2GGrid>
            </P2GGrid>
          </P2GBox>
        </P2GGrid>
        {showOptions && options.length > 1 && (
          <Suspense>
            <P2GGrid item xs={12}>
              <P2GBox pt={4} className="p2g-protection-options">
                <P2GGrid container spacing={2} direction={'column-reverse'}>
                  {options.map((o, i) => {
                    const isSelected = selected?.key === o.key || (!selected && o.key === Protection.NONE);
                    return (
                      <P2GGrid key={o.key} item xs={12}>
                        <P2GOptionContainer
                          selected={isSelected}
                          variant="protection"
                          onClick={() => onChange(o.key, true)}
                          translations={translations}
                        >
                          <P2GGrid container alignItems="center" wrap="nowrap">
                            <P2GGrid item flexShrink={1}>
                              <P2GRadio
                                dataTestId={o.key}
                                radioGroup={radioGroup}
                                checked={isSelected}
                                name={radioGroup}
                                value={o.key}
                                label={null}
                                classNames={[isRecommended(o) ? 'recommended' : '']}
                                variant="primary"
                                required
                              />
                            </P2GGrid>
                            <P2GGrid item flexGrow={1}>
                              <ProtectionMessage
                                isCheapestOption={i === 0}
                                extra={o}
                                currency={currency}
                                maxProtection={maxProtection}
                                parcelValue={parcelValue}
                                serviceIncludedProtection={serviceIncludedProtection}
                                maxAmount={o.metadata?.maxProtection || 0}
                              />
                            </P2GGrid>
                          </P2GGrid>
                        </P2GOptionContainer>
                      </P2GGrid>
                    );
                  })}
                </P2GGrid>
              </P2GBox>
            </P2GGrid>
            {!userHasFullyProtected && (
              <P2GGrid item xs={12}>
                <P2GFade in={true} timeout={350}>
                  <P2GAlert type="error" dataTestId="protection-warning-message">
                    <P2GText variant="body1">
                      <Trans i18nKey="PROTECTION_WARNING_MESSAGE" t={t}>
                        We strongly recommend that you protect the full value of your item(s).
                      </Trans>
                    </P2GText>
                  </P2GAlert>
                </P2GFade>
              </P2GGrid>
            )}
          </Suspense>
        )}
        <P2GGrid item xs={12}>
          <P2GText>
            <Trans
              i18nKey="PROTECTION_FIND_OUT_MORE_MESSAGE"
              t={t}
              defaults="<0>Click here</0> to find out more about parcel protection."
              components={[<P2GLink onClick={() => setOpen(true)} />]}
            />
          </P2GText>
          <ParcelProtectionInfo open={open} onClose={() => setOpen(false)} />
        </P2GGrid>
        {errors && errors[radioGroup] && (
          <P2GGrid item xs={12}>
            <P2GText color="error" fontWeight={700}>
              {errors[radioGroup]}
            </P2GText>
          </P2GGrid>
        )}
      </P2GGrid>
    </SmoothLoader>
  );
};
